<template>
  <form
    ref="form"
    novalidate
    class="container px-0 d-flex flex-column justify-content-between needs-validation"
    @submit="submit"
  >
    <div class="flex-grow-1">
      <div class="row">
        <BootstrapFormControl
          v-model="modelName"
          name="Product Offering Name"
          class="col-4"
          required
          :error="errorMessages.get('name')"
          :server-error="serverError"
        />
        <BootstrapFormControl
          :model-value="productName"
          name="Product Name"
          class="col-4"
          required
          disabled
          :error="errorMessages.get('product_id')"
          :server-error="serverError"
        />
        <BootstrapFormControl
          :model-value="productSku"
          name="SKU Code"
          class="col-4"
          required
          disabled
          :error="errorMessages.get('sku')"
          :server-error="serverError"
        />
      </div>
      <div class="row">
        <div class="col-4">
          <div class="relative d-flex flex-column">
            <label class="form-label">
              Measure Type
              <span class="text-danger">*</span>
            </label>
            <BootstrapRadio
              id="automatic"
              v-model="modelMeasureType"
              value="automatic"
              label="Automatic"
              name="Measure Type"
            />
            <BootstrapRadio
              id="manual"
              v-model="modelMeasureType"
              value="manual"
              label="Manual"
              name="Measure Type"
            />
          </div>
        </div>
        <BootstrapFormControl
          v-model="modelMeasureUnit"
          name="Measure Unit"
          class="col-4"
          required
          :error="errorMessages.get('measure_unit')"
          :server-error="serverError"
        />
        <BootstrapFormControl
          v-model="modelSkuPart"
          name="SKU Part"
          class="col-4"
          required
          :error="errorMessages.get('sku_part')"
          :server-error="serverError"
        />
      </div>
      <div class="row">
        <div class="col-4">
          <div class="form-check">
            <input
              id="hasSphereContent"
              v-model="modelHasSphereContent"
              class="form-check-input"
              type="checkbox"
              name="Has Sphere Content"
            />
            <label class="form-check-label" for="hasSphereContent"> Has Sphere Content </label>
          </div>
        </div>
      </div>
      <div class="row">
        <BootstrapFormControl
          v-model="modelDescription"
          name="Product Offering Description"
          class="col"
          textarea
          required
          :error="errorMessages.get('description')"
          :server-error="serverError"
        />
      </div>
      <div class="row">
        <div class="col-8">
          <BootstrapFormControl
            v-model="modelMeasureDescription"
            name="Measure Method Description"
            class="col"
            textarea
            required
            :error="errorMessages.get('measure_method')"
            :server-error="serverError"
          />
        </div>
        <div class="col-4">
          <div class="row">
            <BootstrapFormControl
              v-model="modelMinCommit"
              name="Minimum Commit"
              class="col"
              placeholder="0"
              required
              :error="errorMessages.get('minimal_commit')"
              :server-error="serverError"
            />
          </div>
          <div class="row">
            <BootstrapFormControl
              v-model="modelMaxCommit"
              name="Maximum Commit (0 = unlimited)"
              class="col"
              placeholder="0"
              required
              :error="errorMessages.get('maximum_commit')"
              :server-error="serverError"
            />
          </div>
        </div>
      </div>
    </div>
    <div class="d-flex w-100">
      <BootstrapButton
        name="submit"
        class="me-2"
        type="submit"
        :disabled="loading"
        :loading="loading"
        @click.stop
      >
        Save
      </BootstrapButton>
      <BootstrapButton name="cancel" variant="secondary" @click="router.go(-1)">
        Cancel
      </BootstrapButton>
    </div>
  </form>
</template>
<script lang="ts" setup>
const props = defineProps<{
  description: string;
  errorMessages: Map<any, any>;
  loading: boolean;
  maxCommit: number;
  measureDescription: string;
  measureType: string;
  measureUnit: string;
  minCommit: number;
  name: string;
  productId: number;
  productName: string;
  productSku: string;
  serverError: boolean;
  skuPart: string;
  hasSphereContent: boolean;
}>();

const emit = defineEmits<{
  'update:description': [value: string];
  'update:maxCommit': [value: number];
  'update:measureDescription': [value: string];
  'update:measureType': [value: string];
  'update:measureUnit': [value: string];
  'update:minCommit': [value: number];
  'update:name': [value: string];
  'update:skuPart': [value: string];
  'update:hasSphereContent': [value: boolean];
  submit: [form: globalThis.Ref<HTMLFormElement | null>];
}>();

const router = useRouter();

// Template ref
const form = ref<HTMLFormElement | null>(null);

// Models
const modelDescription = computed({
  get: () => props.description,
  set: (value) => emit('update:description', value),
});
const modelMaxCommit = computed({
  get: () => String(props.maxCommit),
  set: (value) => emit('update:maxCommit', Number(value)),
});
const modelMeasureDescription = computed({
  get: () => props.measureDescription,
  set: (value) => emit('update:measureDescription', value),
});
const modelMeasureType = computed({
  get: () => props.measureType,
  set: (value) => emit('update:measureType', value),
});
const modelMeasureUnit = computed({
  get: () => props.measureUnit,
  set: (value) => emit('update:measureUnit', value),
});
const modelMinCommit = computed({
  get: () => String(props.minCommit),
  set: (value) => emit('update:minCommit', Number(value)),
});
const modelName = computed({
  get: () => props.name,
  set: (value) => emit('update:name', value),
});
const modelSkuPart = computed({
  get: () => props.skuPart,
  set: (value) => emit('update:skuPart', value),
});
const modelHasSphereContent = computed({
  get: () => props.hasSphereContent,
  set: (value) => emit('update:hasSphereContent', value),
});

// https://getbootstrap.com/docs/4.6/components/forms/?#how-it-works
const submit = async (event: Event) => {
  if (form.value?.checkValidity() === false) {
    event.preventDefault();
    event.stopPropagation();
    form.value.classList.add('was-validated');
  } else {
    event.preventDefault();
    emit('submit', form);
  }
};
</script>
